import { ExpandMore } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useState } from 'react';
import { BettingType, StyleObj } from '../../@types';
import { BETSLIP_WIDTH } from '../../constants';
import { BetslipEvent, useBetslipContext } from '../../contexts/BetslipContext';
import {
  useBetslipDrawerState,
  useBetslipMenuOptions,
  useBetslipStakeErrors,
  useBetslipDisabledState,
  usePlaceBet,
  usePendingBetslipTracker,
  useBetslipStakeAmounts,
  useBetslipCalculations,
  useBetslipCombinations,
  useBetslipEffects,
  useBetslipMenuHandlers,
  useBetslipPayload,
  useBetslipStakeValidation,
  useBetslipSystemTicket,
  StakeAmountInput,
} from '../../hooks/useBetslip';
import BetslipBetCount from '../atoms/BetslipBetCount';
import BetslipErrorMessage from '../atoms/BetslipErrorMessage';
import BetslipInfoMessage from '../atoms/BetslipInfoMessage';
import BetslipInput from '../atoms/BetslipInput';
import OpenBetslipButton from '../molecules/OpenBetslipButton';
import BetslipMenu from '../molecules/menus/BetslipMenu';
import BetslipItem from './BetslipItem';
import BetBuilderBetslipItem from './BetBuilderBetslipItem';
import BetslipPlaceBetButton from './BetslipPlaceBetButton';
import { BetslipWinningAmountDisplay } from '../molecules/BetslipWinningAmountDisplay';
import SignInButton from '../atoms/SignInButton';
import UserSelect from '../molecules/UserSelect';
import FullScreenLoader from '../atoms/FullScreenLoader';
import { FormattedBalance } from '../atoms/FormattedBalance';
import useLocalization from '../../hooks/useLocalization';
import { useRoles } from '../../hooks/useRoles';

const styles: StyleObj = {
  drawer: {
    textAlign: 'center',
    '& .MuiPaper-root': {
      width: {
        xs: '100%',
        sm: BETSLIP_WIDTH,
      },
      background: (theme) => theme.palette.neutral[50],
      borderRadius: {
        xs: 0,
        sm: '4px 4px 0 0',
      },
      margin: 'auto',
      mb: {
        xs: 0,
        md: 4,
      },
    },
    '& form': { maxHeight: '80vh', display: 'flex', flexDirection: 'column' },
  },
  closeButton: {
    borderRadius: 0,
    borderLeft: (theme) => `1px solid ${theme.palette.neutral[100]}`,
    p: 1.5,
  },
  betStack: {
    overflowY: 'auto',
  },
  removeAllButton: { minHeight: 0, p: 0, pl: 1.5, ':hover': { background: 'none' } },
  systemIcon: {
    cursor: 'pointer',
    fontSize: 16,
    color: 'neutral.600',
  },
  signInButton: {
    backgroundColor: 'primary.main',
    borderRadius: 0,
    color: 'inherit',
    typography: 'h5',
    '&:hover': { backgroundColor: 'primary.700' },
    minHeight: 44,
    border: 'none',
    width: '100%',
  },
};

const BetslipDrawer = () => {
  const [disabledSystems, setDisabledSystems] = useState<string[]>([]);

  const { t } = useLocalization();
  const { isAgent } = useRoles();

  const {
    open,
    setOpen,
    showSystemBetInputs,
    toggleSystemBetInputs,
    selectedMenuItem,
    setSelectedMenuItem,
    selectedPlayer,
    setSelectedPlayer,
    closeBetslipDrawer,
  } = useBetslipDrawerState();

  const { localizedBankerMenuOptions, localizedWalletTypeOptions } = useBetslipMenuOptions();
  const { stakeErrors, setStakeErrors, removeSingleStakeError, clearStakeErrors } = useBetslipStakeErrors();

  const {
    bets,
    removeAllBets,
    errors,
    infoMessages,
    updateSinglesStakeAmount,
    reofferedBettingSlipsIds,
    betslipTicketType,
    selectedWallet,
    isWaysTicket,
    currentBankerCount,
    hasBetBuilderSelection,
  } = useBetslipContext();

  const discardBetslip = useCallback(() => {
    closeBetslipDrawer();
    removeAllBets();
    setSelectedPlayer(null);
  }, [closeBetslipDrawer, removeAllBets, setSelectedPlayer]);

  const { hasPendingBetslips } = usePendingBetslipTracker({
    onAllBetslipsResolved: discardBetslip,
  });

  const { stakeAmounts, setStakeAmounts, updateStakeAmount, resetSystemStakeAmountValues } = useBetslipStakeAmounts({
    removeSingleStakeError,
  });

  const { placeBetMutation } = usePlaceBet({
    discardBetslip,
    onSuccess: () => setStakeAmounts({} as StakeAmountInput),
  });

  const { betTypes, preparedBets, maxPayout, totalStakeAmount, totalOdds } = useBetslipCalculations(stakeAmounts);
  const { isSystemTicket } = useBetslipSystemTicket(betTypes);

  const { baseDisabled, disabledByRole, showInsufficientFunds, isAuthenticated } = useBetslipDisabledState({
    totalStakeAmount,
    isAgent,
    selectedPlayerId: selectedPlayer?.id,
    hasPendingBetslips,
  });

  const isPlaceBetButtonDisabled = baseDisabled || disabledByRole || placeBetMutation.isPending;
  const hasReofferedBetslips = reofferedBettingSlipsIds.length > 0;
  const drawerOpen = open || bets.length === 0;
  const hasBetBuilderRow = hasBetBuilderSelection;
  const resolvedSelectedMenuItem = hasBetBuilderRow ? 'normal' : selectedMenuItem;
  const disabledBankerOptions = isWaysTicket || hasBetBuilderRow || bets.length < 3 ? ['bankers'] : [];
  const showSystemSummary = bets.length > 1;
  const showSystemToggle = showSystemSummary && !hasBetBuilderRow;
  const showSystemSection = !hasBetBuilderRow && showSystemBetInputs;
  const systemSummaryLabel = hasBetBuilderRow ? t('multiple') : `${t('multiple')}/${t('system')}`;

  const combinations = useBetslipCombinations(preparedBets, currentBankerCount, showSystemBetInputs, drawerOpen);

  const lastCombinationIndex = useMemo(() => Object.entries(combinations).length - 1, [combinations]);
  const [lastCombinationKey, lastCombinationValue] = useMemo(
    () => Object.entries(combinations)[lastCombinationIndex] || [],
    [combinations, lastCombinationIndex]
  );

  const { handleStakeChange, updateStakes } = useBetslipStakeValidation({
    totalOdds,
    preparedBets,
    disabledSystems,
    setDisabledSystems,
    stakeErrors,
    setStakeErrors,
    stakeAmounts,
    updateStakeAmount,
    updateSinglesStakeAmount,
    combinations,
    removeSingleStakeError,
  });

  const { handleMenuChange, handleWalletChange } = useBetslipMenuHandlers(setSelectedMenuItem, setStakeAmounts);

  const { preparePayload } = useBetslipPayload({
    betTypes,
    totalStakeAmount,
    isAgent,
    selectedPlayerId: selectedPlayer?.id,
  });

  const placeBet = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const payload = preparePayload();
      placeBetMutation.mutate(payload);
    },
    [preparePayload, placeBetMutation]
  );

  useBetslipEffects(
    selectedMenuItem,
    setSelectedMenuItem,
    closeBetslipDrawer,
    resetSystemStakeAmountValues,
    clearStakeErrors
  );

  const renderBetslipRow = useCallback(
    (bet: BetslipEvent) => {
      const value = bet.singlesStakeAmount?.toString() || '';
      const ticketType = bet.isLive ? 'inPlay' : 'preMatch';
      const handleSingleStakeChange = (stakeValue: string) =>
        handleStakeChange(bet.outcomeId, stakeValue, updateSinglesStakeAmount, true, ticketType);

      if (bet.betBuilder) {
        const handleBetBuilderRemove = (outcomeId: string) => {
          removeSingleStakeError(outcomeId);
          removeSingleStakeError(bet.outcomeId);
        };

        return (
          <BetBuilderBetslipItem
            key={bet.outcomeId}
            bet={bet}
            value={value}
            selectedWallet={selectedWallet}
            onChange={handleSingleStakeChange}
            onRemoveBet={handleBetBuilderRemove}
            errorMessages={stakeErrors[bet.outcomeId]}
          />
        );
      }

      return (
        <BetslipItem
          key={bet.outcomeId}
          bet={bet}
          selectedMenuItem={resolvedSelectedMenuItem}
          value={value}
          selectedWallet={selectedWallet}
          onChange={handleSingleStakeChange}
          onRemoveBet={() => removeSingleStakeError(bet.outcomeId)}
          errorMessages={stakeErrors[bet.outcomeId]}
        />
      );
    },
    [
      handleStakeChange,
      removeSingleStakeError,
      resolvedSelectedMenuItem,
      selectedWallet,
      stakeErrors,
      updateSinglesStakeAmount,
    ]
  );

  return (
    <>
      <OpenBetslipButton open={drawerOpen} setOpen={setOpen} />
      <FullScreenLoader open={hasPendingBetslips} />
      <Drawer
        anchor='bottom'
        elevation={0}
        open={open && !hasReofferedBetslips}
        onClose={closeBetslipDrawer}
        sx={styles.drawer}
      >
        <form onSubmit={placeBet}>
          {Array.from(infoMessages)?.map((message) => <BetslipInfoMessage key={message} message={message} />)}
          <Stack
            direction='row'
            spacing={1}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <BetslipBetCount
              numberOfBets={bets.length}
              sx={{
                pl: 3,
              }}
            />
            <Stack
              sx={{
                alignItems: 'end',
                flexGrow: 1,
              }}
            >
              <BetslipMenu
                selectedMenuItem={selectedWallet}
                onChange={handleWalletChange}
                options={localizedWalletTypeOptions}
                sx={{ mr: -1.5 }}
              />

              <Typography
                variant='body2'
                sx={{
                  fontWeight: 600,
                  color: 'neutral.600',
                }}
              >
                <FormattedBalance balanceType={selectedWallet} />
              </Typography>
            </Stack>
            <IconButton sx={styles.closeButton} onClick={closeBetslipDrawer}>
              <ExpandMore />
            </IconButton>
          </Stack>
          <Divider />
          <Stack
            direction='row'
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button variant='text' disableRipple sx={styles.removeAllButton} onClick={discardBetslip}>
              <Typography variant='body3' color='primary'>
                {t('removeAll')}
              </Typography>
            </Button>
            <BetslipMenu
              selectedMenuItem={resolvedSelectedMenuItem}
              onChange={handleMenuChange}
              options={localizedBankerMenuOptions}
              disabledOptions={disabledBankerOptions}
            />
          </Stack>
          <Divider />
          {isAgent && (
            <>
              <Stack>
                <UserSelect selectedUser={selectedPlayer} onUserSelect={setSelectedPlayer} />
              </Stack>
              <Divider />
            </>
          )}
          <Stack sx={styles.betStack}>{bets.map(renderBetslipRow)}</Stack>
          <Divider />
          {showSystemSummary && (
            <Stack
              direction='row'
              sx={{
                alignItems: 'center',
              }}
            >
              <Typography
                variant='body3'
                sx={{
                  color: 'neutral.600',
                  pl: 1.5,
                  pr: 0.25,
                  py: 1,
                  textAlign: 'left',
                }}
              >
                {systemSummaryLabel}
              </Typography>
              {showSystemToggle && (
                <ExpandMore
                  onClick={toggleSystemBetInputs}
                  sx={{ ...styles.systemIcon, ...(showSystemBetInputs && { transform: 'rotate(180deg)' }) }}
                />
              )}
            </Stack>
          )}
          {showSystemSection &&
            Object.entries(combinations).map(
              ([key, combinationValue]) =>
                key !== lastCombinationKey && (
                  <BetslipInput
                    key={key}
                    bettingType={key as BettingType}
                    numberOfCombinations={combinationValue}
                    value={stakeAmounts[key as BettingType]}
                    selectedWallet={selectedWallet}
                    onChange={(value) =>
                      handleStakeChange(key, value, updateStakeAmount, false, betslipTicketType, combinationValue)
                    }
                    errorMessage={stakeErrors[key]?.errorType}
                  />
                )
            )}
          {bets.length > 1 && (
            <BetslipInput
              bettingType={lastCombinationKey as BettingType}
              odds={!isWaysTicket && resolvedSelectedMenuItem === 'normal' ? totalOdds : ''}
              numberOfCombinations={lastCombinationValue}
              value={stakeAmounts[lastCombinationKey as BettingType]}
              selectedWallet={selectedWallet}
              onChange={(value) =>
                handleStakeChange(
                  lastCombinationKey as BettingType,
                  value,
                  updateStakeAmount,
                  false,
                  betslipTicketType,
                  lastCombinationValue
                )
              }
              errorMessage={stakeErrors[lastCombinationKey as string]?.errorType}
            />
          )}
          <Divider />

          {showInsufficientFunds && <BetslipErrorMessage message={t('insufficientFunds')} />}
          {errors.map((message, index) => (
            <BetslipErrorMessage key={`betslip-error-message-${index}`} message={message} />
          ))}

          <BetslipWinningAmountDisplay
            maxPayout={maxPayout}
            isSystemTicket={isSystemTicket}
            selectedWallet={selectedWallet}
          />
          {!isAuthenticated ? (
            <SignInButton sx={styles.signInButton} />
          ) : (
            <BetslipPlaceBetButton
              stakeErrors={stakeErrors}
              totalStakeAmount={totalStakeAmount}
              updateStakes={updateStakes}
              isDisabled={isPlaceBetButtonDisabled}
              disabledSystems={disabledSystems}
            />
          )}
        </form>
      </Drawer>
    </>
  );
};

export default BetslipDrawer;
